import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  User, 
  Moon, 
  Sun, 
  LogOut,
  Mail,
  Tags,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Lock,
  KeyRound,
  MapPin,
  Database,
  Download,
  FileJson,
  HardDrive
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { user, token, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [sectors, setSectors] = useState([]);
  const [loadingSectors, setLoadingSectors] = useState(true);
  const [showAddSector, setShowAddSector] = useState(false);
  const [showEditSector, setShowEditSector] = useState(false);
  const [editingSector, setEditingSector] = useState(null);
  const [deleteSectorDialog, setDeleteSectorDialog] = useState({ open: false, sector: null });
  const [sectorName, setSectorName] = useState('');
  const [savingSector, setSavingSector] = useState(false);
  
  // Areas state
  const [areas, setAreas] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(true);
  const [showAddArea, setShowAddArea] = useState(false);
  const [showEditArea, setShowEditArea] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [deleteAreaDialog, setDeleteAreaDialog] = useState({ open: false, area: null });
  const [areaName, setAreaName] = useState('');
  const [savingArea, setSavingArea] = useState(false);
  
  // Change Password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const fetchSectors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sectors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSectors(response.data);
    } catch (error) {
      console.error('Failed to fetch sectors:', error);
    } finally {
      setLoadingSectors(false);
    }
  }, [token]);

  const fetchAreas = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/areas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAreas(response.data);
    } catch (error) {
      console.error('Failed to fetch areas:', error);
    } finally {
      setLoadingAreas(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSectors();
    fetchAreas();
  }, [fetchSectors, fetchAreas]);

  const handleAddSector = async () => {
    if (!sectorName.trim()) {
      toast.error('Please enter a sector name');
      return;
    }

    setSavingSector(true);
    try {
      await axios.post(`${API}/sectors`, 
        { name: sectorName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Sector added successfully');
      fetchSectors();
      setShowAddSector(false);
      setSectorName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add sector');
    } finally {
      setSavingSector(false);
    }
  };

  const handleEditSector = async () => {
    if (!sectorName.trim() || !editingSector) {
      toast.error('Please enter a sector name');
      return;
    }

    setSavingSector(true);
    try {
      await axios.put(`${API}/sectors/${editingSector.sector_id}`, 
        { name: sectorName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Sector updated successfully');
      fetchSectors();
      setShowEditSector(false);
      setEditingSector(null);
      setSectorName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update sector');
    } finally {
      setSavingSector(false);
    }
  };

  const handleDeleteSector = async () => {
    if (!deleteSectorDialog.sector) return;

    try {
      await axios.delete(`${API}/sectors/${deleteSectorDialog.sector.sector_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sector deleted successfully');
      fetchSectors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sector');
    } finally {
      setDeleteSectorDialog({ open: false, sector: null });
    }
  };

  const openEditDialog = (sector) => {
    setEditingSector(sector);
    setSectorName(sector.name);
    setShowEditSector(true);
  };

  // Area handlers
  const handleAddArea = async () => {
    if (!areaName.trim()) {
      toast.error('Please enter an area name');
      return;
    }

    setSavingArea(true);
    try {
      await axios.post(`${API}/areas`, 
        { name: areaName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Area added successfully');
      fetchAreas();
      setShowAddArea(false);
      setAreaName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add area');
    } finally {
      setSavingArea(false);
    }
  };

  const handleEditArea = async () => {
    if (!areaName.trim() || !editingArea) {
      toast.error('Please enter an area name');
      return;
    }

    setSavingArea(true);
    try {
      await axios.put(`${API}/areas/${editingArea.area_id}`, 
        { name: areaName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Area updated successfully');
      fetchAreas();
      setShowEditArea(false);
      setEditingArea(null);
      setAreaName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update area');
    } finally {
      setSavingArea(false);
    }
  };

  const handleDeleteArea = async () => {
    if (!deleteAreaDialog.area) return;

    try {
      await axios.delete(`${API}/areas/${deleteAreaDialog.area.area_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Area deleted successfully');
      fetchAreas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete area');
    } finally {
      setDeleteAreaDialog({ open: false, area: null });
    }
  };

  const openEditAreaDialog = (area) => {
    setEditingArea(area);
    setAreaName(area.name);
    setShowEditArea(true);
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, 
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl mx-auto" data-testid="settings-page">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage your account and preferences
        </p>
      </div>

      {/* Profile Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Profile
          </CardTitle>
          <CardDescription>Your account information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="text-lg">
                {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-lg">{user?.name || 'User'}</p>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="w-4 h-4" />
                <span>{user?.email || 'No email'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Appearance Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            {theme === 'dark' ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-primary" />}
            Appearance
          </CardTitle>
          <CardDescription>Customize how the app looks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="dark-mode" className="text-base">Dark Mode</Label>
              <p className="text-sm text-muted-foreground">
                Switch between light and dark themes
              </p>
            </div>
            <Switch
              id="dark-mode"
              checked={theme === 'dark'}
              onCheckedChange={toggleTheme}
              data-testid="theme-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Sectors Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Tags className="w-5 h-5 text-primary" />
                Sectors / Industries
              </CardTitle>
              <CardDescription>Manage supplier categories</CardDescription>
            </div>
            <Button 
              size="sm" 
              onClick={() => { setSectorName(''); setShowAddSector(true); }}
              data-testid="add-sector-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Sector
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingSectors ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : sectors.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No sectors configured. Add your first sector to get started.
            </p>
          ) : (
            <div className="space-y-2">
              {sectors.map((sector) => (
                <div 
                  key={sector.sector_id} 
                  className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                  data-testid={`sector-${sector.sector_id}`}
                >
                  <span className="font-medium">{sector.name}</span>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => openEditDialog(sector)}
                      data-testid={`edit-sector-${sector.sector_id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => setDeleteSectorDialog({ open: true, sector })}
                      className="text-red-500 hover:text-red-600"
                      data-testid={`delete-sector-${sector.sector_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Areas Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Area's Covered
              </CardTitle>
              <CardDescription>Manage supplier coverage areas</CardDescription>
            </div>
            <Button 
              size="sm" 
              onClick={() => { setAreaName(''); setShowAddArea(true); }}
              data-testid="add-area-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Area
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingAreas ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : areas.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No areas configured. Add your first area to get started.
            </p>
          ) : (
            <div className="space-y-2">
              {areas.map((area) => (
                <div 
                  key={area.area_id} 
                  className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                  data-testid={`area-${area.area_id}`}
                >
                  <span className="font-medium">{area.name}</span>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => openEditAreaDialog(area)}
                      data-testid={`edit-area-${area.area_id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => setDeleteAreaDialog({ open: true, area })}
                      className="text-red-500 hover:text-red-600"
                      data-testid={`delete-area-${area.area_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Export Section */}
      <Card data-testid="data-export-section">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Database className="w-5 h-5 text-primary" />
            Data Export
          </CardTitle>
          <CardDescription>Download your data for backup or migration to another platform</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Full Database Export */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-start gap-3">
              <HardDrive className="w-8 h-8 text-blue-500 mt-1" />
              <div className="space-y-0.5">
                <p className="font-medium">Full Database Export</p>
                <p className="text-sm text-muted-foreground">
                  Download all data including suppliers, documents metadata, sectors, areas, ratings, and notifications
                </p>
              </div>
            </div>
            <Button 
              variant="outline"
              onClick={async () => {
                try {
                  const response = await axios.get(`${API}/export/database`, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `necos_database_export_${new Date().toISOString().split('T')[0]}.json`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('Database exported successfully');
                } catch (error) {
                  console.error('Export failed:', error);
                  toast.error('Failed to export database');
                }
              }}
              data-testid="export-full-database-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download JSON
            </Button>
          </div>

          {/* Suppliers with Related Data */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-start gap-3">
              <FileJson className="w-8 h-8 text-green-500 mt-1" />
              <div className="space-y-0.5">
                <p className="font-medium">Suppliers Export (with Documents & Ratings)</p>
                <p className="text-sm text-muted-foreground">
                  Download all suppliers with their associated documents and ratings - ideal for importing to another system
                </p>
              </div>
            </div>
            <Button 
              variant="outline"
              onClick={async () => {
                try {
                  const response = await axios.get(`${API}/export/suppliers-json`, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `suppliers_export_${new Date().toISOString().split('T')[0]}.json`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('Suppliers exported successfully');
                } catch (error) {
                  console.error('Export failed:', error);
                  toast.error('Failed to export suppliers');
                }
              }}
              data-testid="export-suppliers-json-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download JSON
            </Button>
          </div>

          {/* Info Note */}
          <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
            <p><strong>Note:</strong> The exported JSON files contain all supplier information that can be used to recreate suppliers in another platform. Document files themselves are not included in the export - only their metadata.</p>
          </div>
        </CardContent>
      </Card>

      {/* Security Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-primary" />
            Security
          </CardTitle>
          <CardDescription>Manage your password and security settings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="font-medium">Password</p>
              <p className="text-sm text-muted-foreground">
                Change your account password
              </p>
            </div>
            <Button 
              variant="outline"
              onClick={() => setShowChangePassword(true)}
              data-testid="change-password-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              Change Password
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">Account</CardTitle>
          <CardDescription>Manage your account</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Separator />
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="font-medium">Sign Out</p>
              <p className="text-sm text-muted-foreground">
                Sign out of your account on this device
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={logout}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Add Sector Dialog */}
      <Dialog open={showAddSector} onOpenChange={setShowAddSector}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Sector</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="sector-name">Sector Name</Label>
            <Input
              id="sector-name"
              placeholder="e.g., Automotive, Agriculture"
              value={sectorName}
              onChange={(e) => setSectorName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddSector()}
              className="mt-2"
              data-testid="sector-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSector(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSector} disabled={savingSector} data-testid="save-sector-btn">
              {savingSector ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Sector'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sector Dialog */}
      <Dialog open={showEditSector} onOpenChange={setShowEditSector}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Sector</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="edit-sector-name">Sector Name</Label>
            <Input
              id="edit-sector-name"
              value={sectorName}
              onChange={(e) => setSectorName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleEditSector()}
              className="mt-2"
              data-testid="edit-sector-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditSector(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSector} disabled={savingSector} data-testid="update-sector-btn">
              {savingSector ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Sector'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Sector Confirmation */}
      <AlertDialog 
        open={deleteSectorDialog.open} 
        onOpenChange={(open) => setDeleteSectorDialog({ open, sector: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sector</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteSectorDialog.sector?.name}"? 
              This action cannot be undone. Sectors with assigned suppliers cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSector} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Area Dialog */}
      <Dialog open={showAddArea} onOpenChange={setShowAddArea}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Area</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="area-name">Area Name</Label>
            <Input
              id="area-name"
              placeholder="e.g., London, Manchester, Nationwide"
              value={areaName}
              onChange={(e) => setAreaName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddArea()}
              className="mt-2"
              data-testid="area-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddArea(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddArea} disabled={savingArea} data-testid="save-area-btn">
              {savingArea ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Area'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Area Dialog */}
      <Dialog open={showEditArea} onOpenChange={setShowEditArea}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Area</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="edit-area-name">Area Name</Label>
            <Input
              id="edit-area-name"
              value={areaName}
              onChange={(e) => setAreaName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleEditArea()}
              className="mt-2"
              data-testid="edit-area-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditArea(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditArea} disabled={savingArea} data-testid="update-area-btn">
              {savingArea ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Area'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Area Confirmation */}
      <AlertDialog 
        open={deleteAreaDialog.open} 
        onOpenChange={(open) => setDeleteAreaDialog({ open, area: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Area</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteAreaDialog.area?.name}"? 
              This action cannot be undone. Areas with assigned suppliers cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteArea} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={(open) => {
        setShowChangePassword(open);
        if (!open) {
          setCurrentPassword('');
          setNewPassword('');
          setConfirmNewPassword('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="pl-10"
                  placeholder="••••••••"
                  data-testid="current-password-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10"
                  placeholder="••••••••"
                  data-testid="new-password-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">Must be at least 8 characters</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-new-password">Confirm New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirm-new-password"
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="pl-10"
                  placeholder="••••••••"
                  data-testid="confirm-new-password-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangePassword(false)}>
              Cancel
            </Button>
            <Button onClick={handleChangePassword} disabled={changingPassword} data-testid="save-password-btn">
              {changingPassword ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Changing...
                </>
              ) : (
                'Change Password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
